/**
 * Global authentication context.
 *
 * Exposes:
 *   • user          Firebase user object (or `null` when signed out)
 *   • loading       Flag while auth state is being resolved on boot
 *   • claims        Decoded ID token claims (or null)
 *   • clinicToken   Convenience alias for claims?.clinic
 *   • refreshClaims Forces a token refresh + claims reload
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAuth, onIdTokenChanged, getIdTokenResult } from "firebase/auth";
import { firebaseApp } from "../firebase/firebaseApp";

// Provide sensible defaults to avoid undefined checks in consumers.
const AuthContext = createContext({
  user: null,
  loading: true,
  claims: null,
  clinicToken: null,
  refreshClaims: async () => {},
});

/**
 * React provider that supplies auth state + decoded claims to its subtree.
 */
export function AuthProvider({ children }) {
  const auth = getAuth(firebaseApp);

  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Subscribe to ID token changes (initial + refresh/claim updates).   */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setClaims(null);
        setLoading(false);
        setBootstrapped(true);
        return;
      }

      setUser(firebaseUser);
      try {
        const token = await getIdTokenResult(
          firebaseUser,
          /* forceRefresh */ false
        );
        setClaims(token.claims || null);
      } catch (_) {
        setClaims(null);
      } finally {
        setLoading(false);
        setBootstrapped(true);
      }
    });

    return unsubscribe;
  }, [auth]);

  /* ------------------------------------------------------------------ */
  /* Manual refresher for claims (e.g., after redeeming an invite).     */
  /* ------------------------------------------------------------------ */
  const refreshClaims = async () => {
    if (!auth.currentUser) return null;
    const u = auth.currentUser;
    // Force a fresh token from the server so new claims appear
    await u.getIdToken(true);
    const token = await getIdTokenResult(u, false);
    setClaims(token.claims || null);
    return token.claims || null;
  };

  const clinicToken = claims?.clinic ?? null;

  /* ------------------------------------------------------------------ */
  /* Memoise context to keep reference stable between renders.          */
  /* ------------------------------------------------------------------ */
  const contextValue = useMemo(
    () => ({ user, loading, claims, clinicToken, refreshClaims }),
    [user, loading, claims, clinicToken]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Delay rendering children until first auth resolution completes. */}
      {bootstrapped ? children : null}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook for consuming the authentication context.
 */
export const useAuth = () => useContext(AuthContext);
